import { AuthService } from '@auth0/auth0-angular';

import { inject, Injectable } from '@angular/core';
import { combineLatest, firstValueFrom, tap, timeout } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { devLog, TOKEN_REQUEST_EVENT, TOKEN_SYNC_EVENT } from './jwt.constant';
import { CONTEXT_ID_CLAIM, CUSTOMER_ID_CLAIM, MijnJwtService } from './mijn-jwt.service';
import { SESSION_STORAGE_CACHE, SessionStorageCache } from './session-storage-cache';

@Injectable()
export class AuthTokenSyncService {
    private readonly auth = inject(AuthService);
    private readonly jwtService = inject(MijnJwtService);
    private readonly cache = inject<SessionStorageCache>(SESSION_STORAGE_CACHE, { optional: true });
    private currentToken: string | null = null;
    private bootstrapResolved = false;
    private pendingTokenRequests = 0;

    public install(): Promise<void> {
        devLog('[AuthTokenSync] install() called'); // NOSONAR
        this.jwtService.setObservables(
            this.auth.isAuthenticated$,
            this.auth.idTokenClaims$.pipe(map((c) => c?.[CUSTOMER_ID_CLAIM] ?? null)),
            this.auth.idTokenClaims$.pipe(map((c) => c?.[CONTEXT_ID_CLAIM] ?? null))
        );

        // Register TOKEN_REQUEST_EVENT listener before bootstrap so any webcomponent
        // that dispatches TOKEN_REQUEST_EVENT after bootstrap has already completed
        // will always receive a response with the current token.
        // If bootstrap is still in progress, defer the response until it resolves
        // to avoid broadcasting null and causing a token flicker in the webcomponent.
        window.addEventListener(TOKEN_REQUEST_EVENT, () => {
            if (!this.bootstrapResolved) {
                devLog('[AuthTokenSync] TOKEN_REQUEST_EVENT received during bootstrap, deferring response'); // NOSONAR
                this.pendingTokenRequests++;
                return;
            }
            devLog('[AuthTokenSync] TOKEN_REQUEST_EVENT received, responding with token:', this.currentToken ? 'present' : 'null'); // NOSONAR
            this.broadcastToken(this.currentToken);
        });

        this.auth.isAuthenticated$.pipe(filter((isAuthenticated) => !isAuthenticated)).subscribe(() => {
            devLog('[AuthTokenSync] isAuthenticated$ became false, clearing token and cache'); // NOSONAR
            this.cache?.clearAll();
            this.setAndBroadcastToken(null);
        });

        this.auth.error$
            .pipe(filter((err) => (err as any)?.error === 'invalid_grant' || (err as any)?.error === 'login_required'))
            .subscribe((err) => {
                devLog('[AuthTokenSync] refresh token invalid/expired, redirecting to login:', err); // NOSONAR
                this.auth
                    .loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } })
                    .pipe(take(1))
                    .subscribe();
            });

        return firstValueFrom(
            combineLatest([this.auth.isLoading$, this.auth.isAuthenticated$]).pipe(
                tap(
                    ([loading, isAuthenticated]) => devLog('[AuthTokenSync] isLoading$:', loading, 'isAuthenticated$:', isAuthenticated) // NOSONAR
                ),
                filter(([loading]) => !loading),
                switchMap(([, isAuthenticated]) => {
                    if (!isAuthenticated) {
                        devLog('[AuthTokenSync] not authenticated, resolving bootstrap'); // NOSONAR
                        return [null];
                    }
                    devLog('[AuthTokenSync] authenticated, waiting for access token before resolving bootstrap'); // NOSONAR
                    return this.auth.getAccessTokenSilently().pipe(
                        tap((token) => {
                            devLog('[AuthTokenSync] bootstrap access token received, token present:', !!token); // NOSONAR
                            this.setAndBroadcastToken(token as string);
                        }),
                        map((token) => token as string),
                        catchError((err) => {
                            devLog('[AuthTokenSync] bootstrap getAccessTokenSilently() error, resolving anyway:', err); // NOSONAR
                            this.setAndBroadcastToken(null);
                            return [null];
                        })
                    );
                }),
                timeout({
                    each: 10000,
                    with: () => {
                        devLog('[AuthTokenSync] bootstrap timed out after 10s, resolving anyway'); // NOSONAR
                        return [null];
                    }
                })
            )
        ).then(() => {
            this.bootstrapResolved = true;
            devLog('[AuthTokenSync] install() promise resolved, bootstrap unblocked'); // NOSONAR
            if (this.pendingTokenRequests > 0) {
                devLog(
                    '[AuthTokenSync] flushing',
                    this.pendingTokenRequests,
                    'deferred TOKEN_REQUEST_EVENT(s), token:',
                    this.currentToken ? 'present' : 'null'
                ); // NOSONAR
                this.broadcastToken(this.currentToken);
                this.pendingTokenRequests = 0;
            }
        });
    }

    private setAndBroadcastToken(token: string | null): void {
        this.currentToken = token;
        this.jwtService.setToken(token);
        this.broadcastToken(token);
    }

    private broadcastToken(token: string | null): void {
        devLog('[AuthTokenSync] broadcasting TOKEN_SYNC_EVENT, token:', token ? 'present' : 'null'); // NOSONAR
        window.dispatchEvent(new CustomEvent(TOKEN_SYNC_EVENT, { detail: token }));
    }
}
